import { authHeaders } from './session';
import { downloadBlob } from './csv';

/**
 * The API client.
 *
 * design.md §15.6 puts generated contracts in `packages/contracts` (Zod schemas
 * + an OpenAPI client). That package does not exist yet and this file is its
 * placeholder in the honest sense: hand-written types that mirror the API's
 * responses, in one file, so there is exactly one place to replace when the
 * generator arrives. It is not a stub — everything here works.
 */

import type {
  Coverage,
  LocationHolding,
  LocationKind,
  LocationType,
  StorageLocation,
} from './locations';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The parsed error body, where there was one — 409 carries `currentVersion`. */
    readonly body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** CAT-007 — somebody else saved first. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** Field-level validation messages from the API's Zod filter. */
  get issues(): { field: string; message: string }[] {
    const raw = this.body?.issues;
    return Array.isArray(raw) ? (raw as { field: string; message: string }[]) : [];
  }

  /** No credential, or one the API did not accept. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Authenticated, but not for a tenant this API knows. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * Filters to query-string values.  CAT-013
 *
 * Booleans become the literal strings the API's tri-state parser expects, and
 * `undefined` survives as `undefined` so `request` drops the key entirely —
 * sending `hasImage=` would be a filter for products with no image, which is
 * the opposite of not filtering.
 */
/**
 * Turn a channel-scoped filter into a set of SKUs.  journeys P18/S2, P18/S3
 *
 * §3.1 lets `catalog` depend on `platform` only, so the product list cannot ask
 * whether something is published (that is `commerce`) or in stock (that is
 * `warehouse`). Each module already answers its own question for a channel, so
 * the answer is resolved here and handed to the list as a plain set of SKUs —
 * see `ProductFilters.skus` on the API side.
 *
 * `null` means "no channel-scoped filter is active"; an empty array means "the
 * filter is active and nothing matched", which must still narrow the list to
 * nothing rather than being mistaken for no filter at all.
 */
export interface SkuSelection {
  /**
   * Only these SKUs. An EMPTY array is a real answer — "the filter is on and
   * nothing matched" — and must narrow the list to nothing. Undefined means no
   * opinion.
   */
  skus?: string[] | undefined;
  /** Everything except these. Empty and undefined both mean "exclude nothing". */
  notSkus?: string[] | undefined;
}

export async function resolveSkuFilter(filters: ProductFilters): Promise<SkuSelection | null> {
  const channel = filters.saleChannel;
  if (!channel || (!filters.onSale && !filters.stock)) return null;

  const selection: SkuSelection = {};

  if (filters.onSale) {
    const published = await api.channelListings(channel, 'published');
    const live = published.items.map((row) => row.sku);
    // On sale is an include set; not on sale is the SAME set, excluded. The
    // complement is never computed here: doing that would mean pulling the
    // whole catalogue into the browser to subtract from, which is both a
    // second round trip and a silent cap at the list's page size.
    if (filters.onSale === 'on') selection.skus = live;
    else selection.notSkus = live;
  }

  if (filters.stock) {
    // Zero for "out", the shop's low-stock number for "low" — the same
    // endpoint the Availability screen reads, so the two always agree.
    const short = await api.shortfalls(channel, filters.stock === 'out' ? 0 : LOW_STOCK, 500);
    const shortSkus = short.items.map((row) => row.productSku);
    // Both filters on means both must hold. Two include sets intersect here;
    // an include set and an exclusion are simply both sent, because the list
    // ANDs them.
    selection.skus =
      selection.skus === undefined
        ? shortSkus
        : selection.skus.filter((sku) => shortSkus.includes(sku));
  }

  return selection;
}

/**
 * Every product the current filters match, paged until there are no more.
 *
 * The list is keyset-paged and the export needs all of it, so this walks the
 * cursor rather than asking for one enormous page — the API caps a page at 200
 * for the same reason the export caps its total.
 */
export async function fetchAllProducts(
  filters: ProductFilters & { selection?: SkuSelection | null },
  lang: string,
  cap: number,
): Promise<ProductListItem[]> {
  const collected: ProductListItem[] = [];
  let cursor: string | undefined;
  // A bound on the loop as well as on the rows: a cursor that stopped advancing
  // would otherwise spin against the server forever.
  for (let guard = 0; guard <= cap / 200 + 1; guard += 1) {
    const page = await api.products({ ...filters, limit: 200, cursor, lang });
    collected.push(...page.items);
    if (!page.nextCursor || collected.length >= cap) return collected.slice(0, cap);
    cursor = page.nextCursor;
  }
  return collected.slice(0, cap);
}

/** Whether a resolved selection can match anything at all. */
export function selectsNothing(selection: SkuSelection | null | undefined): boolean {
  return selection?.skus !== undefined && selection.skus.length === 0;
}

/**
 * What "low" means until somebody sets it per product.
 *
 * A constant with a name rather than a 5 in a query: S5 of the stock journey
 * asks for a per-product threshold, and when it exists this is where the
 * default lives rather than a number nobody can find.
 */
export const LOW_STOCK = 5;

function filterParams(filters: ProductFilters): Record<string, string | undefined> {
  const bool = (value: boolean | undefined) => (value === undefined ? undefined : String(value));
  return {
    q: filters.q,
    status: filters.status,
    category: filters.category,
    hasImage: bool(filters.hasImage),
    hasBarcode: bool(filters.hasBarcode),
    hasWeight: bool(filters.hasWeight),
    missingTranslation: filters.missingTranslation,
    missingKeywords: filters.missingKeywords,
    // CAT-053 — the `p.` prefix keeps a property called `status` from shadowing
    // the status filter. Same names in the URL, so the two cannot drift.
    ...Object.fromEntries(
      Object.entries(filters.properties ?? {}).map(([key, value]) => [`p.${key}`, value]),
    ),
  };
}

/**
 * A GET, with its query string.
 *
 * THE SECOND ARGUMENT IS QUERY PARAMETERS, NOT FETCH OPTIONS, and `method`/`body`
 * are banned from it explicitly. That is not fussiness: passing
 * `{ method: 'PUT', body: … }` here typechecked happily and turned into
 * `?method=PUT&body=…` on a GET. It shipped, and the first thing it did was put a
 * live Stripe key in a URL — which means in the CDN log, the load balancer log
 * and the browser history, and the key had to be rotated.
 *
 * Writes go through `send`, which takes a real init and stringifies the body.
 */
const WORKBOOK_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * The filename the server asked for, out of `Content-Disposition`.
 *
 * Deliberately narrow: it reads the quoted `filename=` form, which is the only
 * one this API sends, and returns null for anything else rather than guessing.
 * The caller then falls back to a name it can build itself.
 */
function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="([^"]+)"/.exec(header);
  return match?.[1] ?? null;
}

async function request<T>(
  path: string,
  params?: Record<string, string | number | string[] | undefined> & {
    method?: never;
    body?: never;
  },
) {
  const url = new URL(path, globalThis.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    // An array repeats the key — `?sku=a&sku=b` — which is what Express parses
    // back into an array and what the API's schemas expect. `set` would keep
    // only the last one, and the caller would silently ask about one SKU.
    if (Array.isArray(value)) {
      for (const each of value) url.searchParams.append(key, each);
    } else if (value !== undefined && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }
  return send<T>(url.toString(), { method: 'GET' });
}

async function send<T>(
  path: string,
  init: { method: string; body?: unknown } = { method: 'GET' },
): Promise<T> {
  const response = await fetch(path, {
    method: init.method,
    headers: {
      Accept: 'application/json',
      ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...authHeaders(),
    },
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  });

  if (!response.ok) {
    let message = response.statusText;
    let body: Record<string, unknown> | undefined;
    try {
      body = (await response.json()) as Record<string, unknown>;
      if (typeof body.message === 'string') message = body.message;
    } catch {
      // A non-JSON error body is not worth failing over; the status carries it.
    }
    throw new ApiError(response.status, message, body);
  }

  // 204 has no body, and JSON.parse('') throws.
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * A typed decimal like "19,90" or "19.90" into integer minor units.
 *
 * Parsed from the string, not via `Number(x) * 100`: `Number('1.005') * 100` is
 * `100.49999999999999`, which floors to a cent short. Same reasoning as the
 * ingest's parser and `packages/pricing/src/money.ts` — money never touches a
 * float on the way in.
 *
 * Returns null for anything that is not a number, so the caller can show a
 * validation message instead of writing a silent zero.
 */
export function parseMoneyToMinor(input: string): number | null {
  const text = input.trim().replace(',', '.');
  if (text === '') return null;
  if (!/^\d+(\.\d{0,2})?$/.test(text)) return null;

  const [whole = '0', fraction = ''] = text.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
}

/** Minor units back into an editable decimal string. */
export function minorToInput(minor: number): string {
  return (minor / 100).toFixed(2);
}

// --- shapes ----------------------------------------------------------------

export interface CatalogSummary {
  products: number;
  variants: number;
  categories: number;
  media: number;
  translationCoverage: {
    languageCode: string;
    isDefault: boolean;
    translated: number;
    total: number;
  }[];
}

export interface Category {
  code: string;
  name: string | null;
  parentCode: string | null;
  productCount: number;
}

/**
 * A physical measurement on the way to the API: the text as typed, a number, or
 * null to clear it. Parsed server-side — see `updateVariant`.
 */
export type Measurement = string | number | null;

export interface ProductListItem {
  id: string;
  sku: string;
  status: string;
  name: string | null;
  /**
   * The default language's name, when this one has none.  journeys P18/S4
   *
   * Search spans every translation while the list displays one, so a search in
   * a language the catalogue barely has returns real hits whose Name column is
   * empty. Kept separate from `name` so the row can say it IS a fallback rather
   * than quietly looking translated.
   */
  nameFallback: { text: string; language: string } | null;
  /** Where it lives: the URL, the breadcrumb and the property schema (0050). */
  categoryCode: string | null;
  defaultVariantId: string | null;
  variantCount: number;
  imageCount: number;
  /** CAT-010 — row thumbnail. Null when there is no image whose bytes exist. */
  image: { url: string; previewUrl: string; alt: string | null } | null;
  /**
   * CAT-012 — why this row is in the results, or null when not searching.
   *
   * The API has always said, and until now nothing read it. It earns its place
   * on the row for `barcode`: the SKU is a column and a name match is visible
   * in one, but a barcode is neither, so that row otherwise looks like a
   * mistake in the search.
   */
  matchedField: 'sku' | 'barcode' | 'text' | null;
}

/** One derived size per key, plus the untouched upload. §15.5 */
export interface MediaUrls {
  original: string;
  thumb: string;
  card: string;
  view: string;
}

// --- activity (CAT-095) -----------------------------------------------------

/** One recorded change. §15.10: actor, entity, before, after, timestamp. */
export interface AuditEntry {
  id: string;
  occurredAt: string;
  actorSubject: string;
  actorEmail: string | null;
  actorKind: string;
  entityType: string;
  entityId: string | null;
  entityLabel: string;
  action: string;
  field: string | null;
  before: unknown;
  after: unknown;
  reason: string | null;
}

export interface ActivityResponse {
  items: AuditEntry[];
  /** Only values that actually occur, so a filter never returns nothing. */
  fields: string[];
  actors: { subject: string; email: string | null }[];
}

// --- properties (Epic F) ----------------------------------------------------

export interface PropertyDefinition {
  id: string;
  key: string;
  dataType: 'integer' | 'decimal' | 'boolean' | 'text' | 'enum' | 'multi_enum' | 'date';
  unit: string | null;
  group: string | null;
  isFilterable: boolean;
  isVisibleOnStorefront: boolean;
  /** Value lives in a typed variant column, not in jsonb. Read-only here. */
  isBuiltin: boolean;
  builtinColumn: string | null;
  sortOrder: number;
  displayName: string;
  description: string | null;
  placeholder: string | null;
  options: { value: string; label: string; sortOrder: number }[];
  /** CAT-050 — how many products and variants hold a value for this key. */
  usageCount: number;
}

export interface ResolvedProperty {
  key: string;
  definition: PropertyDefinition;
  schemaCode: string | null;
  schemaName: string | null;
  isRequired: boolean;
  sortOrder: number;
  value: unknown;
  /** Formatted with its unit, e.g. "10 kΩ". */
  display: string | null;
  /** Stored, but no attached schema explains it. */
  isExtra: boolean;
}

export interface ProductProperties {
  productId: string;
  sku: string;
  fields: ResolvedProperty[];
  variants: { id: string; sku: string; fields: ResolvedProperty[] }[];
}

/** CAT-051 — where a schema is attached. */
export interface PropertySchemaBinding {
  id: string;
  targetType: 'category' | 'product' | 'variant';
  targetCode: string;
  targetLabel: string;
}

export interface PropertyDefinitionInput {
  key: string;
  dataType: PropertyDefinition['dataType'];
  unit?: string | null;
  group?: string | null;
  isFilterable?: boolean;
  isVisibleOnStorefront?: boolean;
  translations?: { languageCode: string; displayName: string; description?: string | null }[];
  options?: { value: string; sortOrder?: number; labels?: Record<string, string> }[];
}

// --- bulk fill (CAT-055) ----------------------------------------------------

export interface FillRow {
  id: string;
  sku: string;
  name: string | null;
  imageUrl: string | null;
  value: unknown;
  display: string | null;
  /** The other properties already filled, for answering the question. */
  context: { label: string; display: string }[];
}

export interface FillQueue {
  schemaCode: string;
  schemaName: string;
  level: 'product' | 'variant';
  property: {
    key: string;
    displayName: string;
    dataType: string;
    unit: string | null;
    isBuiltin: boolean;
    options: { value: string; label: string }[];
  };
  progress: { filled: number; total: number };
  items: FillRow[];
  truncated: boolean;
}

export interface FillResult {
  sku: string;
  ok: boolean;
  problem?: string;
  value?: unknown;
}

// --- coverage and impact (CAT-057, CAT-056) ---------------------------------

export interface CoverageProperty {
  key: string;
  displayName: string;
  unit: string | null;
  dataType: string;
  isBuiltin: boolean;
  isRequired: boolean;
  filled: number;
  total: number;
}

export interface CoverageSchema {
  code: string;
  name: string;
  level: 'product' | 'variant';
  reached: number;
  /** Cells, summed over every property in the schema. */
  filled: number;
  total: number;
  properties: CoverageProperty[];
}

/** CAT-056 — what a proposed member list would do. */
export interface MemberImpact {
  level: 'product' | 'variant';
  reached: number;
  added: { key: string; displayName: string; isRequired: boolean; missing: number }[];
  removed: { key: string; displayName: string; orphaned: number }[];
  incomplete: number;
}

/** CAT-056 — what a definition change would do to the values already stored. */
export interface DefinitionImpact {
  key: string;
  values: number;
  dataType: { from: string; to: string } | null;
  failing: number;
  unit: { from: string | null; to: string | null } | null;
  needsReview: number;
}

/** CAT-053 — a filterable property, and what there is to filter on. */
export interface PropertyFacet {
  key: string;
  displayName: string;
  dataType: string;
  unit: string | null;
  group: string | null;
  isBuiltin: boolean;
  options: { value: string; label: string }[];
  min: number | null;
  max: number | null;
  productCount: number;
}

export interface PropertySchemaSummary {
  id: string;
  code: string;
  name: string;
  description: string | null;
  level: 'product' | 'variant';
  isActive: boolean;
  memberCount: number;
  usageCount: number;
}

// --- channels and listings (§6.3, architecture §10.8) -----------------------

export type ListingState = 'draft' | 'published' | 'hidden' | 'delisted';
export type SeoSource = 'authored' | 'imported' | 'templated' | 'derived';

export interface ListingProblem {
  code: 'missing-translation' | 'no-price' | 'no-image';
  message: string;
  /** Blocks publishing, as opposed to merely being worth knowing. */
  blocking: boolean;
}

/** Where one product is sold, counted. See `listingSummaries`. */
export interface ListingSummary {
  sku: string;
  published: number;
  hidden: number;
  channels: number;
}

export interface ProductListing {
  /** The absolute URL, or null when the channel has no origin set. */
  publicUrl: string | null;
  channelCode: string;
  channelName: string;
  locale: string;
  languageCode: string;
  currency: string;
  state: ListingState;
  publishedAt: string | null;
  externalUrl: string | null;
  /**
   * Resolved, with the origin of each value: explicit → template → derived.
   * The source is what makes "which of these did a human write" answerable
   * after the RankMath scrape is deleted (§10.8).
   */
  seo: {
    slug: { value: string; source: SeoSource };
    title: { value: string; source: SeoSource };
    description: { value: string; source: SeoSource } | null;
    noindex: boolean;
  } | null;
  problems: ListingProblem[];
}

export interface Channel {
  code: string;
  name: string;
  siteCode: string;
  kind: 'online' | 'shop';
  locale: string;
  currency: string;
  brand: string | null;
  seoTitleTemplate: string | null;
  seoDescriptionTemplate: string | null;
  defaultProfileCode: string | null;
  /** The name it answers to on the open internet, or null when unpublished. */
  publicCode: string | null;
  /** §19.2 — whether this channel's storefront submits carts and orders here. */
  acceptsOrders: boolean;
  /**
   * What this channel's order numbers begin with, or null for bare numbers.
   * §4.5.3, migration 0046
   *
   * Order numbers count from 1 per channel, so a tenant with two shops has two
   * orders called "1" unless one of them carries a prefix. Null is right for
   * the shop with one channel and is the default.
   */
  orderNumberPrefix: string | null;
  /**
   * Where this channel's storefront lives, e.g. `https://protocache.com`.
   * migration 0048
   *
   * Null when unknown, and then the back office shows a product's path with no
   * link — a wrong link on a publish confirmation is worse than none.
   */
  storefrontOrigin: string | null;
  mailLogoUrl: string | null;
  mailSupportEmail: string | null;
  /** Printed on customer-facing documents — 0065. */
  supportPhone: string | null;
  mailLegalFooter: string | null;
  bankTransferIban: string | null;
  bankTransferBic: string | null;
  bankTransferBeneficiary: string | null;
  paymentMethodLabel: string | null;
  listings: { published: number; draft: number; hidden: number; delisted: number };
}

export interface Site {
  code: string;
  name: string;
  city: string | null;
}

// --- delivery (design.md §6.4) -----------------------------------------------

export interface ShippingBand {
  minQty: number;
  /** Exclusive of VAT, minor units. */
  priceMinor: number;
}

export interface ShippingMethod {
  id: string;
  channelCode: string;
  code: string;
  name: string;
  sortOrder: number;
  active: boolean;
  rateKind: 'flat' | 'quantity_bands';
  flatMinor: number | null;
  /** Null means never free. */
  freeOverMinor: number | null;
  /**
   * Which subtotal the threshold is compared against.
   *
   * The rule measured on production is against the item subtotal INCLUDING
   * VAT; reading it ex-VAT tells a customer to spend more than they need to.
   */
  freeOverBasis: 'items_gross' | 'items_net';
  freeOverAfterDiscount: boolean;
  taxRateSource: 'highest_line' | 'fixed';
  fixedRateBp: number | null;
  bands: ShippingBand[];
}

export interface ShippingMethodInput {
  code: string;
  name: string;
  sortOrder?: number;
  active?: boolean;
  rateKind: 'flat' | 'quantity_bands';
  flatMinor?: number | null;
  freeOverMinor?: number | null;
  freeOverBasis?: 'items_gross' | 'items_net';
  freeOverAfterDiscount?: boolean;
  taxRateSource?: 'highest_line' | 'fixed';
  fixedRateBp?: number | null;
  bands?: ShippingBand[];
}

// --- stock (design.md §10) ---------------------------------------------------

export interface StockAvailability {
  variantId: string;
  sku: string;
  /** False when the ledger has no opinion, and then the rest is not a limit. */
  tracked: boolean;
  onHand: number;
  allocated: number;
  available: number;
  outOfStock: boolean;
}

/**
 * A line on the availability screen.  §6.4, §9.6
 *
 * Availability belongs to a channel and a variant together, which is why this
 * has its own screen with a picker rather than a column in the product list: a
 * column would have to choose a channel silently and be wrong for every other.
 */
export interface StockShortfall extends StockAvailability {
  productSku: string;
  name: string | null;
}

export interface StockMovement {
  id: string;
  qtyDelta: number;
  reason: string;
  reference: string | null;
  note: string | null;
  actor: string;
  occurredAt: string;
  siteCode: string;
}

export interface ChannelListingRow {
  sku: string;
  name: string | null;
  state: ListingState;
  slug: string;
}

// --- categories (Epic I) ----------------------------------------------------

export interface CategoryNode {
  id: string;
  code: string;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  name: string | null;
  slug: string | null;
  productCount: number;
  /** CAT-070 — the rollup, which differs from productCount once a tree exists. */
  productCountWithDescendants: number;
  depth: number;
  children: CategoryNode[];
}

export interface CategoryDetail {
  id: string;
  code: string;
  parentId: string | null;
  parentCode: string | null;
  sortOrder: number;
  isActive: boolean;
  productCount: number;
  productCountWithDescendants: number;
  version: string;
  updatedAt: string;
  translations: {
    languageCode: string;
    name: string;
    slug: string;
    description: string | null;
    /** The live public address. CAT-072's "current live URL". */
    publicPath: string;
  }[];
  media: { id: string; isPrimary: boolean; altText: string | null; urls: MediaUrls | null }[];
}

export interface MoveImpact {
  categoryCode: string;
  fromParentCode: string | null;
  toParentCode: string | null;
  directProducts: number;
  descendantProducts: number;
  descendantCategories: number;
}

export interface SlugImpact {
  languageCode: string;
  currentSlug: string;
  currentPath: string;
  nextSlug: string;
  nextPath: string;
  conflict: boolean;
  conflictingCategoryCode: string | null;
}

/** CAT-073 — an old→new pair waiting for the CDN. */
export interface UrlRedirect {
  id: string;
  fromPath: string;
  toPath: string;
  entityType: string;
  createdAt: string;
}

/** CAT-060 — an image attached to a product. */
export interface ProductMedia {
  /**
   * Alt text by language code (0049), e.g. `{ fi: "…", sv: "…" }`.
   *
   * All of them at once so the media panel can switch language without a
   * request, and so it can say which ones are still missing.
   */
  altByLanguage: Record<string, string>;
  id: string;
  sha256: string;
  ext: string;
  isPrimary: boolean;
  /** False when the row describes bytes that were never copied into the store. */
  objectPresent: boolean;
  displayOrder: number;
  altText: string | null;
  width: number | null;
  height: number | null;
  byteSize: number | null;
  urls: MediaUrls | null;
}

/** §7.1 — one entry per rule that contributed, in the order it applied. */
export interface TraceStep {
  step: 'base' | 'rule' | 'break' | 'override' | 'vat' | 'rounding' | 'clamp';
  label: string;
  ref?: string;
  resultMinor: number;
}

export type PriceResult =
  | {
      ok: true;
      price: { minor: number; currency: string };
      netMinor: number;
      vat: {
        percent: number;
        amountMinor: number;
        destinationCountry: string;
        assumed: boolean;
        /** False when no rate row matched — distinct from a genuine 0%. */
        rateFound: boolean;
      };
      inclusive: boolean;
      source: TraceStep[];
    }
  | { ok: false; reason: string; detail: string };

export interface ResolvedPrices {
  channel: string;
  profile: string;
  currency: string;
  rows: { variantId: string; result: PriceResult }[];
}

export interface Variant {
  id: string;
  sku: string;
  barcode: string | null;
  uom: string;
  isDefault: boolean;
  isActive: boolean;
  weightGrams: number | null;
  /**
   * How many of this may be bought at once.  readiness B6; migration 0047
   *
   * Valid quantities are `min`, `min + step`, `min + 2*step`, … up to `max`.
   * A bag of 100 screws is `min: 100, step: 100`; a shop that does not sell in
   * quantity leaves all three at their defaults and never sees them.
   */
  minOrderQty: number;
  maxOrderQty: number | null;
  orderStep: number;
  lengthMm: number | null;
  widthMm: number | null;
  heightMm: number | null;
  updatedAt: string;
  version: string;
}

export interface ProductPage {
  items: ProductListItem[];
  /** §8 keyset cursor. null means this is the last page. */
  nextCursor: string | null;
}

export interface ProductDetail extends ProductListItem {
  /**
   * Where else this product can be FOUND.  migration 0050
   *
   * `categoryCode` is where it LIVES — one category, because the URL, the
   * breadcrumb and the property schema each need exactly one answer. These are
   * membership only and never include the primary.
   */
  alsoInCategories: string[];
  properties: Record<string, unknown>;
  taxClass: string;
  updatedAt: string;
  /** Opaque concurrency token — send it back on update (CAT-007). */
  version: string;
  translations: {
    languageCode: string;
    name: string;
    descriptionShort: string | null;
    /**
     * The long one. HOLDS HTML — the imported catalogue has `<p>` and
     * `<strong>` in 38 of 57 products, and the storefront renders it rather
     * than escaping it. Anything that displays this in the back office has to
     * treat it as source text, not as a paragraph to render.
     */
    description: string | null;
    keywords: string | null;
  }[];
  variants: Variant[];
  media: ProductMedia[];
}

/**
 * CAT-011. `sortFields` is what the server can actually order by, and the table
 * makes a header clickable only if it appears here — so a column can never
 * advertise a sort the backend would reject. `price` and `stock` are absent on
 * purpose; the API's sort.ts explains why.
 */
export interface CatalogMeta {
  sortFields: SortField[];
  sortDirections: SortDirection[];
  defaultSort: { field: SortField; direction: SortDirection };
}

export type SortField = 'sku' | 'name' | 'status' | 'created' | 'updated';
export type SortDirection = 'asc' | 'desc';

/**
 * CAT-013. Everything that narrows the list, and nothing that pages or orders
 * it — the count endpoint takes exactly this, so the number beside the filters
 * always describes the same set the table is showing.
 *
 * The tri-states are `undefined` for "don't care", not `false`. `false` is a
 * filter in its own right: "products with no image" is the one that matters.
 */
export interface ProductFilters {
  q?: string | undefined;
  status?: string | undefined;
  category?: string | undefined;
  /**
   * Which shop the two channel-scoped filters below are about.
   *
   * "On sale" and "in stock" are not properties of a product — they are
   * properties of a product ON A CHANNEL, and a shop with two of them gets two
   * different answers. So the filters carry their channel, and the control is
   * only shown when there is more than one to choose between.
   */
  saleChannel?: string | undefined;
  /** `on` = published on that channel, `off` = not. See `resolveSkuFilter`. */
  onSale?: 'on' | 'off' | undefined;
  /** `out` = nothing sellable, `low` = at or below the low-stock number. */
  stock?: 'out' | 'low' | undefined;
  hasImage?: boolean | undefined;
  hasBarcode?: boolean | undefined;
  hasWeight?: boolean | undefined;
  missingTranslation?: string | undefined;
  /**
   * Products with no keywords in this language.
   *
   * Keywords are how a product is found by words that are not in its name —
   * descriptions are deliberately not indexed — so this is the queue of
   * products that can only be found by name and SKU. journeys P18/S1
   */
  missingKeywords?: string | undefined;
  /**
   * CAT-053 — property facets, keyed by property.
   *
   * The value is the API's own expression: `3000..12000` for a range,
   * `round|d_flat` for a set of codes, `true` for a flag. Kept as the string
   * rather than a parsed object because it goes to the URL and to the API
   * unchanged, and a second representation in between is a second thing to
   * keep in step.
   */
  properties?: Record<string, string> | undefined;
}

export interface ProductQuery extends ProductFilters {
  limit?: number;
  cursor?: string | undefined;
  lang?: string;
  sort?: SortField | undefined;
  dir?: SortDirection | undefined;
}

// --- calls -----------------------------------------------------------------

/** The tenant's tax position. migration 0073. */
export interface TaxSettings {
  homeCountry: string;
  /**
   * `YYYY-MM-DD`, or null for a seller that has never been VAT-liable.
   *
   * A DATE RATHER THAN A FLAG, because documents raised before a registration
   * must go on being classified the way they were when they were issued.
   */
  vatRegisteredFrom: string | null;
  vatLiableToday: boolean;
}

/** A stored secret, as much of it as can ever be shown. migration 0070. */
export interface StoredCredential {
  name: string;
  /** The first characters of a SHA-256 of the value. Not a prefix of the value. */
  fingerprint: string;
  note: string | null;
  updatedAt: string;
}

/** Which processor a shop settles through. migration 0075. */
export interface PspSettings {
  provider: 'stripe';
  /** Names a stored credential, or null when the key has not arrived yet. */
  credentialRef: string | null;
}

export interface SettlementReportLine {
  sourceRef: string | null;
  chargeRef: string | null;
  kind: string;
  chargedAt: string;
  gross: number;
  fee: number;
  net: number;
  orderNo: string | null;
  orderId: string | null;
  customerEmail: string | null;
  orderGross: number | null;
}

export interface SettlementReportPayout {
  payoutRef: string;
  arrivedOn: string;
  /** What the bank statement says. */
  net: number;
  status: string;
  lines: SettlementReportLine[];
  linesNet: number;
  unexplained: number;
  unmatched: number;
}

export interface SettlementReport {
  window: { from: string; to: string };
  provider: string;
  currency: string | null;
  payouts: SettlementReportPayout[];
  totals: { salesGross: number; processingFees: number; otherNet: number; paidOut: number };
  agrees: boolean;
}

export const api = {
  summary: () => request<CatalogSummary>('/v1/catalog/summary'),

  /** What this deployment can sort by. See CatalogMeta. */
  meta: () => request<CatalogMeta>('/v1/catalog/meta'),

  categories: (lang = 'fi') => request<{ items: Category[] }>('/v1/catalog/categories', { lang }),

  products: (query: ProductQuery & { selection?: SkuSelection | null }) =>
    request<ProductPage>('/v1/catalog/products', {
      ...filterParams(query),
      // See `resolveSkuFilter`. An empty include set never reaches the wire —
      // it appends nothing to a query string and would read as "no filter" —
      // so callers short-circuit on `selectsNothing` instead.
      sku: query.selection?.skus,
      notSku: query.selection?.notSkus,
      limit: query.limit ?? 50,
      cursor: query.cursor,
      lang: query.lang ?? 'fi',
      sort: query.sort,
      dir: query.dir,
    }),

  /** CAT-013 — how many match, ignoring paging. */
  productCount: (filters: ProductFilters & { selection?: SkuSelection | null }, lang = 'fi') =>
    request<{ total: number }>('/v1/catalog/products/count', {
      ...filterParams(filters),
      sku: filters.selection?.skus,
      notSku: filters.selection?.notSkus,
      lang,
    }),

  product: (sku: string, lang = 'fi') =>
    request<ProductDetail>(`/v1/catalog/products/${encodeURIComponent(sku)}`, { lang }),

  /**
   * §3.1 keeps pricing in `commerce` and out of `catalog`, so the price for a
   * list of products is a second call and the composition happens here. POST
   * because a page of 250 variant ids does not fit comfortably in a URL.
   */
  prices: async (variantIds: string[], options: { quantity?: number } = {}) => {
    if (variantIds.length === 0) {
      return { channel: '', profile: '', currency: '', rows: [] } satisfies ResolvedPrices;
    }
    const response = await fetch('/v1/commerce/prices/resolve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeaders() },
      body: JSON.stringify({ variantIds, ...options }),
    });
    if (!response.ok) throw new ApiError(response.status, response.statusText);
    return (await response.json()) as ResolvedPrices;
  },

  // --- writes ---------------------------------------------------------------

  /** CAT-030 */
  createProduct: (input: {
    sku: string;
    name: string;
    lang?: string;
    category?: string | null;
    status?: string;
    taxClass?: string;
    price?: { currency: string; minor: number };
  }) => send<{ sku: string }>('/v1/catalog/products', { method: 'POST', body: input }),

  /** CAT-033 */
  updateProduct: (
    sku: string,
    input: {
      /**
       * A rename. The response carries the SKU afterwards, because a product
       * is addressed by it and a rename moves the resource — the caller has
       * to follow, not assume.
       */
      sku?: string;
      status?: string;
      category?: string | null;
      taxClass?: string;
      expectedVersion: string;
    },
  ) =>
    send<{ version: string; sku: string }>(`/v1/catalog/products/${encodeURIComponent(sku)}`, {
      method: 'PATCH',
      body: input,
    }),

  /** CAT-065 */
  putTranslation: (
    sku: string,
    lang: string,
    input: {
      name: string;
      descriptionShort?: string | null;
      description?: string | null;
      keywords?: string | null;
    },
  ) =>
    send<void>(
      `/v1/catalog/products/${encodeURIComponent(sku)}/translations/${encodeURIComponent(lang)}`,
      { method: 'PUT', body: input },
    ),

  deleteTranslation: (sku: string, lang: string) =>
    send<void>(
      `/v1/catalog/products/${encodeURIComponent(sku)}/translations/${encodeURIComponent(lang)}`,
      { method: 'DELETE' },
    ),

  /** The categories a product can also be found in (0050). The whole set. */
  setAdditionalCategories: (sku: string, codes: string[]) =>
    send<void>(`/v1/catalog/products/${encodeURIComponent(sku)}/categories`, {
      method: 'PUT',
      body: { codes },
    }),

  /** CAT-041 */
  /**
   * The four measurements go up AS TYPED.  journeys P11/A1
   *
   * `Number()` in the editor turned `0,25 kg` — and `12,5`, the ordinary
   * Finnish decimal — into NaN, which serialises to null, which this endpoint
   * reads as "clear the value". The API parses the string now, with the same
   * function the bulk fill queue uses, so the two screens cannot disagree and
   * an unreadable entry is an error rather than an erasure.
   */
  updateVariant: (
    id: string,
    input: {
      sku?: string;
      barcode?: string | null;
      uom?: string;
      weightGrams?: Measurement;
      minOrderQty?: number;
      maxOrderQty?: number | null;
      orderStep?: number;
      lengthMm?: Measurement;
      widthMm?: Measurement;
      heightMm?: Measurement;
      isActive?: boolean;
      expectedVersion: string;
    },
  ) => send<{ version: string }>(`/v1/catalog/variants/${id}`, { method: 'PATCH', body: input }),

  /** CAT-040 — add a variant to a product that already exists. */
  createVariant: (
    sku: string,
    input: {
      sku: string;
      barcode?: string | null;
      uom?: string;
      weightGrams?: Measurement;
      lengthMm?: Measurement;
      widthMm?: Measurement;
      heightMm?: Measurement;
    },
  ) =>
    send<{ id: string }>(`/v1/catalog/products/${encodeURIComponent(sku)}/variants`, {
      method: 'POST',
      body: input,
    }),

  deleteVariant: (id: string) => send<void>(`/v1/catalog/variants/${id}`, { method: 'DELETE' }),

  /** CAT-041 — the variant the list prices and the storefront leads with. */
  setDefaultVariant: (id: string) =>
    send<void>(`/v1/catalog/variants/${id}/default`, { method: 'POST' }),

  /** CAT-076 */
  getBasePrices: (variantId: string) =>
    send<{ items: { currency: string; minor: number }[] }>(`/v1/commerce/prices/base/${variantId}`),

  setBasePrice: (variantId: string, currency: string, minor: number | null) =>
    send<void>(`/v1/commerce/prices/base/${variantId}`, {
      method: 'PUT',
      body: { currency, minor },
    }),

  // --- activity (CAT-095) ---------------------------------------------------

  productActivity: (sku: string, filters: { field?: string; actor?: string } = {}) =>
    request<ActivityResponse>(`/v1/catalog/products/${encodeURIComponent(sku)}/activity`, filters),

  categoryActivity: (code: string, filters: { field?: string; actor?: string } = {}) =>
    request<ActivityResponse>(
      `/v1/catalog/categories/${encodeURIComponent(code)}/activity`,
      filters,
    ),

  // --- properties (Epic F) --------------------------------------------------

  propertyDefinitions: (lang = 'fi') =>
    request<{ items: PropertyDefinition[] }>('/v1/catalog/properties/definitions', { lang }),

  propertySchemas: () =>
    request<{ items: PropertySchemaSummary[] }>('/v1/catalog/properties/schemas'),

  createPropertyDefinition: (input: PropertyDefinitionInput) =>
    send<{ id: string }>('/v1/catalog/properties/definitions', { method: 'POST', body: input }),

  updatePropertyDefinition: (
    key: string,
    // CAT-056 — a unit change over existing values is refused until the caller
    // has been shown how many of them it reinterprets.
    input: Partial<PropertyDefinitionInput> & { acknowledgeUnitChange?: boolean },
  ) =>
    send<void>(`/v1/catalog/properties/definitions/${encodeURIComponent(key)}`, {
      method: 'PATCH',
      body: input,
    }),

  deletePropertyDefinition: (key: string) =>
    send<void>(`/v1/catalog/properties/definitions/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    }),

  createPropertySchema: (input: {
    code: string;
    name: string;
    description?: string | null;
    level: 'product' | 'variant';
  }) => send<{ id: string }>('/v1/catalog/properties/schemas', { method: 'POST', body: input }),

  deletePropertySchema: (code: string) =>
    send<void>(`/v1/catalog/properties/schemas/${encodeURIComponent(code)}`, { method: 'DELETE' }),

  /** The complete member list, not a diff. */
  setPropertySchemaMembers: (code: string, members: { key: string; isRequired?: boolean }[]) =>
    send<void>(`/v1/catalog/properties/schemas/${encodeURIComponent(code)}/members`, {
      method: 'PUT',
      body: { members },
    }),

  propertySchemaBindings: (code: string) =>
    request<{ items: PropertySchemaBinding[] }>(
      `/v1/catalog/properties/schemas/${encodeURIComponent(code)}/bindings`,
    ),

  bindPropertySchema: (
    code: string,
    target: { categoryCode?: string; productSku?: string; variantId?: string },
  ) =>
    send<void>(`/v1/catalog/properties/schemas/${encodeURIComponent(code)}/bindings`, {
      method: 'POST',
      body: target,
    }),

  unbindPropertySchema: (
    code: string,
    target: { categoryCode?: string; productSku?: string; variantId?: string },
  ) =>
    send<void>(`/v1/catalog/properties/schemas/${encodeURIComponent(code)}/bindings/remove`, {
      method: 'POST',
      body: target,
    }),

  /** CAT-055 — one row per product for a single property, empty first. */
  fillQueue: (
    schema: string,
    key: string,
    lang = 'fi',
    // CAT-056 — filled-first turns the same queue into a review pass.
    order: 'empty-first' | 'filled-first' = 'empty-first',
  ) => request<FillQueue>('/v1/catalog/properties/fill', { schema, key, lang, order }),

  /** CAT-057 — per schema and per property, how much is actually filled in. */
  propertyCoverage: (lang = 'fi') =>
    request<{ items: CoverageSchema[] }>('/v1/catalog/properties/coverage', { lang }),

  /** CAT-053 — the facets the list panel can offer. */
  propertyFacets: (lang = 'fi') =>
    request<{ items: PropertyFacet[] }>('/v1/catalog/properties/facets', { lang }),

  /** CAT-056 — the impact of a member list, before it is saved. */
  previewPropertySchemaMembers: (
    code: string,
    members: { key: string; isRequired?: boolean }[],
    lang = 'fi',
  ) =>
    send<MemberImpact>(
      `/v1/catalog/properties/schemas/${encodeURIComponent(code)}/members/preview?lang=${lang}`,
      { method: 'POST', body: { members } },
    ),

  /** CAT-056 — the impact of a type or unit change. */
  previewPropertyDefinition: (key: string, input: { dataType?: string; unit?: string | null }) =>
    send<DefinitionImpact>(
      `/v1/catalog/properties/definitions/${encodeURIComponent(key)}/preview`,
      { method: 'POST', body: input },
    ),

  applyFill: (
    schema: string,
    key: string,
    writes: { sku: string; value: string | number | boolean | null; expectedCurrent?: unknown }[],
  ) =>
    send<{ results: FillResult[] }>('/v1/catalog/properties/fill', {
      method: 'PUT',
      body: { schema, key, writes },
    }),

  propertySchemaMembers: (code: string, lang = 'fi') =>
    request<{
      items: { definition: PropertyDefinition; isRequired: boolean; sortOrder: number }[];
    }>(`/v1/catalog/properties/schemas/${encodeURIComponent(code)}/members`, { lang }),

  productProperties: (sku: string, lang = 'fi') =>
    request<ProductProperties>(`/v1/catalog/products/${encodeURIComponent(sku)}/properties`, {
      lang,
    }),

  /** Only the keys present are touched; null clears one. */
  setProductProperties: (sku: string, values: Record<string, unknown>) =>
    send<void>(`/v1/catalog/products/${encodeURIComponent(sku)}/properties`, {
      method: 'PUT',
      body: { values },
    }),

  setVariantProperties: (variantId: string, values: Record<string, unknown>) =>
    send<void>(`/v1/catalog/variants/${variantId}/properties`, {
      method: 'PUT',
      body: { values },
    }),

  // --- categories (Epic I) --------------------------------------------------

  categoryTree: (lang = 'fi') =>
    request<{ items: CategoryNode[] }>('/v1/catalog/categories/tree', { lang }),

  category: (code: string) =>
    request<CategoryDetail>(`/v1/catalog/categories/${encodeURIComponent(code)}`),

  /** CAT-070 — the complete sibling sequence. Saves immediately, no confirm. */
  reorderCategories: (parentCode: string | null, orderedCodes: string[]) =>
    send<void>('/v1/catalog/categories/order', {
      method: 'PUT',
      body: { parentCode, orderedCodes },
    }),

  saveCategoryTranslation: (
    code: string,
    lang: string,
    input: { name: string; description?: string | null },
  ) =>
    send<void>(
      `/v1/catalog/categories/${encodeURIComponent(code)}/translations/${encodeURIComponent(lang)}`,
      { method: 'PUT', body: input },
    ),

  /** CAT-071 */
  previewCategoryMove: (code: string, parentCode: string | null) =>
    send<MoveImpact>(`/v1/catalog/categories/${encodeURIComponent(code)}/move/preview`, {
      method: 'POST',
      body: { parentCode },
    }),

  moveCategory: (code: string, parentCode: string | null) =>
    send<void>(`/v1/catalog/categories/${encodeURIComponent(code)}/move`, {
      method: 'POST',
      body: { parentCode },
    }),

  /** CAT-072 */
  previewCategorySlug: (code: string, lang: string, slug: string) =>
    send<SlugImpact>(`/v1/catalog/categories/${encodeURIComponent(code)}/slug/preview`, {
      method: 'POST',
      body: { lang, slug },
    }),

  setCategorySlug: (code: string, lang: string, slug: string, expectedVersion: string) =>
    send<{ version: string; redirectFrom: string; redirectTo: string }>(
      `/v1/catalog/categories/${encodeURIComponent(code)}/slug`,
      { method: 'PUT', body: { lang, slug, expectedVersion } },
    ),

  /** CAT-074 */
  deleteCategory: (code: string, moveProductsTo: string | null) =>
    send<{ moved: number }>(`/v1/catalog/categories/${encodeURIComponent(code)}/delete`, {
      method: 'POST',
      body: { moveProductsTo },
    }),

  /** CAT-073 */
  redirects: () => request<{ items: UrlRedirect[] }>('/v1/catalog/categories/redirects'),

  deleteRedirect: (id: string) =>
    send<void>(`/v1/catalog/categories/redirects/${id}`, { method: 'DELETE' }),

  // --- media (CAT-060) ------------------------------------------------------

  /**
   * The file goes up as the raw request body, not as multipart.
   *
   * `send` is bypassed because it JSON-encodes everything it is given; this
   * needs the File object itself, and the browser sets Content-Type from it.
   */
  uploadMedia: async (sku: string, file: File): Promise<UploadedMedia> => {
    const url = `/v1/catalog/products/${encodeURIComponent(sku)}/media?filename=${encodeURIComponent(file.name)}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': file.type || 'application/octet-stream',
        ...authHeaders(),
      },
      body: file,
    });
    if (!response.ok) {
      let message = response.statusText;
      let body: Record<string, unknown> | undefined;
      try {
        body = (await response.json()) as Record<string, unknown>;
        if (typeof body.message === 'string') message = body.message;
      } catch {
        // Keep the status as the message.
      }
      throw new ApiError(response.status, message, body);
    }
    return (await response.json()) as UploadedMedia;
  },

  /**
   * Alt text, in one language.  migration 0049
   *
   * The language is not optional here even though the API defaults it: sending
   * the wrong one silently overwrites another language's text, and a default
   * is exactly how that happens.
   */
  setMediaAltText: (sku: string, mediaId: string, lang: string, altText: string | null) =>
    send<void>(`/v1/catalog/products/${encodeURIComponent(sku)}/media/${mediaId}`, {
      method: 'PATCH',
      body: { altText, lang },
    }),

  setPrimaryMedia: (sku: string, mediaId: string) =>
    send<void>(`/v1/catalog/products/${encodeURIComponent(sku)}/media/${mediaId}/primary`, {
      method: 'POST',
    }),

  /** The complete sequence, not a move. */
  reorderMedia: (sku: string, mediaIds: string[]) =>
    send<void>(`/v1/catalog/products/${encodeURIComponent(sku)}/media/order`, {
      method: 'PUT',
      body: { mediaIds },
    }),

  deleteMedia: (sku: string, mediaId: string) =>
    send<void>(`/v1/catalog/products/${encodeURIComponent(sku)}/media/${mediaId}`, {
      method: 'DELETE',
    }),

  // --- channels and listings (§6.3, §10.8) ----------------------------------

  channels: () => request<{ items: Channel[] }>('/v1/commerce/channels'),

  sites: () => request<{ items: Site[] }>('/v1/commerce/sites'),

  // --- delivery (§6.4) ------------------------------------------------------

  shippingMethods: (channel: string) =>
    request<{ items: ShippingMethod[] }>(
      `/v1/commerce/channels/${encodeURIComponent(channel)}/shipping`,
    ),

  /** Create or replace one, bands and all. A rate table is read as a whole. */
  saveShippingMethod: (channel: string, method: ShippingMethodInput) =>
    send<ShippingMethod>(`/v1/commerce/channels/${encodeURIComponent(channel)}/shipping`, {
      method: 'PUT',
      body: method,
    }),

  removeShippingMethod: (channel: string, code: string) =>
    send<void>(
      `/v1/commerce/channels/${encodeURIComponent(channel)}/shipping/${encodeURIComponent(code)}`,
      { method: 'DELETE' },
    ),

  // --- stock (design.md §10) ------------------------------------------------

  /**
   * Availability for a set of SKUs on one channel.
   *
   * Per channel rather than per site because that is the question worth asking:
   * §6.4's figure is on-hand less what is held less the channel's oversell
   * buffer, and a raw per-site number is one an operator has to adjust in their
   * head before acting on it.
   */
  availability: (channel: string, skus: string[]) =>
    request<{ items: StockAvailability[] }>('/v1/stock/availability', { channel, sku: skus }),

  /** What this channel cannot sell. `atOrBelow` 0 is "gone"; higher is reorder. */
  shortfalls: (channel: string, atOrBelow = 0, limit = 200) =>
    request<{ items: StockShortfall[] }>('/v1/stock/shortfalls', { channel, atOrBelow, limit }),

  // --- where things are (0052) ---------------------------------------------

  locations: (site: string) =>
    request<{ items: StorageLocation[] }>('/v1/warehouse/locations', { site }),

  locationCoverage: (site: string) =>
    request<Coverage>('/v1/warehouse/locations/coverage', { site }),

  /** Where is this? Includes the unlocated remainder, which is a real answer. */
  whereIs: (sku: string, site?: string) =>
    request<{ items: LocationHolding[] }>('/v1/warehouse/locations/where', { sku, site }),

  /**
   * The place a scanned bin label's address names, across every site.
   *
   * Plural because a path is unique per site and a scanner does not know which
   * building it is in — two answers is a question for the operator, not a coin
   * toss. See `scan.ts`.
   */
  locationsAt: (path: string) =>
    request<{ items: StorageLocation[] }>('/v1/warehouse/locations/at', { path }),

  locationContents: (id: string) =>
    request<{ items: LocationHolding[] }>(
      `/v1/warehouse/locations/${encodeURIComponent(id)}/contents`,
    ),

  createLocation: (input: {
    site: string;
    parentId?: string | null;
    kind: LocationKind;
    code: string;
    name?: string | null;
    locationType?: LocationType;
  }) => send<StorageLocation>('/v1/warehouse/locations', { method: 'POST', body: input }),

  /** WMS-032 — a rack at a time. All of them or none. */
  createLocations: (input: {
    site: string;
    parentId?: string | null;
    kind: LocationKind;
    codes: string[];
    locationType?: LocationType;
  }) =>
    send<{ items: StorageLocation[] }>('/v1/warehouse/locations/bulk', {
      method: 'POST',
      body: input,
    }),

  updateLocation: (
    id: string,
    patch: {
      code?: string;
      name?: string | null;
      locationType?: LocationType;
      pickSequence?: number | null;
      isActive?: boolean;
    },
  ) =>
    send<StorageLocation>(`/v1/warehouse/locations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: patch,
    }),

  deleteLocation: (id: string) =>
    send<void>(`/v1/warehouse/locations/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /**
   * Put it somewhere, or move it.
   *
   * `fromId: null` is the capture walk — "this was somewhere at this site, and
   * it is on A-3-2-B4 as of now" — which is every entry on the first pass.
   */
  placeStock: (input: {
    sku: string;
    site: string;
    fromId: string | null;
    toId: string;
    qty: number;
    note?: string | null;
  }) =>
    send<{ items: LocationHolding[] }>('/v1/warehouse/locations/move', {
      method: 'POST',
      body: input,
    }),

  stockHistory: (sku: string, limit = 50) =>
    request<{ items: StockMovement[] }>(`/v1/stock/${encodeURIComponent(sku)}/history`, { limit }),

  /** A movement, never a balance. There is no endpoint that sets a number. */
  recordMovement: (input: {
    siteCode: string;
    sku: string;
    qtyDelta: number;
    reason: 'receipt' | 'adjustment' | 'count' | 'return';
    /**
     * Which shelf, when the operator is standing at one.
     *
     * A bin's contents and the site total are two sums over the same ledger,
     * so a correction that names no shelf moves one and not the other.
     */
    locationId?: string | null;
    note?: string;
    reference?: string;
  }) => send<StockAvailability>('/v1/stock/movements', { method: 'POST', body: input }),

  /**
   * Place an order to prove the shop works.  migration 0059
   *
   * Numbered `TEST-n` from its own series, never invoiceable, absent from
   * revenue — and in every other respect a real order, including the stock it
   * holds and the email it sends to `email`.
   */
  /**
   * What to pick, in walking order.  migration 0052
   *
   * Order numbers repeat as `?order=` rather than joining on a comma: an order
   * number is operator-typed text with a configurable prefix, and deciding a
   * comma cannot appear in one is a rule nobody agreed to.
   */
  pickList: (orderNos: string[], channel?: string) =>
    send<PickList>(
      `/v1/warehouse/pick-list?${orderNos.map((n) => `order=${encodeURIComponent(n)}`).join('&')}` +
        (channel ? `&channel=${encodeURIComponent(channel)}` : ''),
    ),

  placeTestOrder: (input: { channel: string; sku: string; qty: number; email: string }) =>
    send<{ id: string; orderNo: string; grandTotal: number; currency: string }>('/v1/orders/test', {
      method: 'POST',
      body: input,
    }),

  updateChannel: (
    code: string,
    input: {
      name?: string;
      brand?: string | null;
      seoTitleTemplate?: string | null;
      seoDescriptionTemplate?: string | null;
      acceptsOrders?: boolean;
      orderNumberPrefix?: string | null;
      storefrontOrigin?: string | null;
      mailLogoUrl?: string | null;
      mailSupportEmail?: string | null;
      supportPhone?: string | null;
      mailLegalFooter?: string | null;
      bankTransferIban?: string | null;
      bankTransferBic?: string | null;
      bankTransferBeneficiary?: string | null;
      paymentMethodLabel?: string | null;
    },
  ) =>
    send<void>(`/v1/commerce/channels/${encodeURIComponent(code)}`, {
      method: 'PUT',
      body: input,
    }),

  /** CAT-080 — every channel, listed or not, with what is blocking each. */
  /**
   * Where a page of products is sold.  §6.3
   *
   * A second request, like prices, and for the same reason: §3.1 forbids
   * `catalog` depending on `commerce`, so the join happens in the browser.
   */
  listingSummaries: (skus: string[]) =>
    request<{ items: ListingSummary[] }>('/v1/commerce/listings/summary', { sku: skus }),

  productListings: (sku: string) =>
    request<{ items: ProductListing[] }>(
      `/v1/commerce/listings/product/${encodeURIComponent(sku)}`,
    ),

  channelListings: (code: string, state?: ListingState) =>
    request<{ items: ChannelListingRow[] }>(
      `/v1/commerce/listings/channel/${encodeURIComponent(code)}`,
      state ? { state } : {},
    ),

  setListingState: (sku: string, channelCode: string, state: ListingState) =>
    send<void>(
      `/v1/commerce/listings/product/${encodeURIComponent(sku)}/channel/${encodeURIComponent(channelCode)}`,
      { method: 'PUT', body: { state } },
    ),

  /**
   * `null` clears an override back to the template or the derived value;
   * omitting a field leaves it alone. Two different intentions.
   */
  setListingContent: (
    sku: string,
    channelCode: string,
    lang: string,
    input: {
      slug?: string | null;
      seoTitle?: string | null;
      seoDescription?: string | null;
      noindex?: boolean;
    },
  ) =>
    send<void>(
      `/v1/commerce/listings/product/${encodeURIComponent(sku)}/channel/${encodeURIComponent(channelCode)}/content/${lang}`,
      { method: 'PUT', body: input },
    ),

  /** CAT-023 — reports per SKU; a blocked one does not sink the batch. */
  bulkSetListingState: (skus: string[], channel: string, state: ListingState) =>
    send<{ results: { sku: string; ok: boolean; problem?: string }[] }>(
      '/v1/commerce/listings/bulk',
      { method: 'PUT', body: { skus, channel, state } },
    ),

  // --- settings: API keys (§5.1) --------------------------------------------

  // --- the storefront sync ---------------------------------------------------

  /** What a channel feeds, and whether it is switched on.  migrations 0067, 0069 */
  storefronts: () => request<{ items: StorefrontSettings[] }>('/v1/settings/storefronts'),

  putStorefront: (channel: string, input: StorefrontSettings) =>
    send<void>(`/v1/settings/storefronts/${encodeURIComponent(channel)}`, {
      method: 'PUT',
      body: input,
    }),

  /**
   * The push queue, product by product.  migration 0066
   *
   * `failed` is the exceptions queue — 0066 said it would be a query rather
   * than another table, and this is that query.
   */
  storefrontQueue: (channel: string, status: QueueStatus = 'all') =>
    request<{ counts: Record<string, number>; items: QueuedProduct[] }>(
      `/v1/settings/storefronts/${encodeURIComponent(channel)}/queue`,
      { status },
    ),

  /** Put the failures back in the queue. See the endpoint for why this is separate. */
  retryStorefront: (channel: string) =>
    send<{ retrying: number }>(`/v1/settings/storefronts/${encodeURIComponent(channel)}/retry`, {
      method: 'POST',
    }),

  /**
   * What the integrations have been doing.  migration 0071
   *
   * Read from lasti rather than from a container log, so it is addressable,
   * tenant-scoped, and available when the platform is not.
   */
  integrationLog: (params: { limit?: number; source?: string; level?: string } = {}) =>
    request<{ items: IntegrationEvent[] }>('/v1/settings/integration-log', params),

  apiKeys: () => request<{ items: ApiKey[] }>('/v1/settings/api-keys'),

  /**
   * Record an invoice raised outside lasti, so it is not raised again.
   * migration 0074.
   *
   * NOT `issueInvoice` WITH A FLAG. That one asks the bookkeeping system to
   * create a document; this one says a document already exists. Nothing is
   * pushed anywhere.
   */
  recordExternalInvoice: (input: {
    orderId: string;
    documentNo: string;
    issuedOn: string;
    reference?: string | null;
    externalRef?: string | null;
    termsDays?: number | null;
  }) => send<Invoice>('/v1/invoices/record', { method: 'POST', body: input }),

  // --- credentials (migration 0070) ------------------------------------------

  /** Names and fingerprints. Never a value; there is no endpoint that returns one. */
  credentials: () => request<{ items: StoredCredential[] }>('/v1/settings/credentials'),

  /**
   * Store a secret under a name, encrypted at rest.
   *
   * The value goes up and is never readable again — the list returns a
   * fingerprint, which is enough to tell two keys apart and useless as one.
   */
  putCredential: (name: string, value: string, note?: string | null) =>
    send<void>(`/v1/settings/credentials/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: { value, note: note ?? null },
    }),

  // --- settlements (migration 0075) ------------------------------------------

  /** Which processor this shop settles through, and whether its key is set. */
  settlementSettings: () => request<{ settings: PspSettings | null }>('/v1/settlements/settings'),

  setSettlementSettings: (input: { provider: 'stripe'; credentialRef: string | null }) =>
    send<{ settings: PspSettings }>('/v1/settlements/settings', { method: 'PUT', body: input }),

  /** One month of payouts, decomposed into the orders that made them up. */
  settlementReport: (month: string) => request<SettlementReport>('/v1/settlements', { month }),

  /**
   * Fetch the workbook and hand it to the browser as a download.
   *
   * A FETCH AND NOT A LINK, which was the first thing this got wrong. An
   * `<a href>` navigation carries no headers, this app authenticates with a
   * bearer token held in memory, and the download answered 401 — correctly.
   *
   * The filename still comes from the server: `Content-Disposition` is read off
   * the response rather than reconstructed here, so the period in the name and
   * the period in the file cannot drift apart.
   */
  downloadSettlementWorkbook: async (month: string): Promise<void> => {
    const url = new URL('/v1/settlements/xlsx', globalThis.location.origin);
    url.searchParams.set('month', month);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { Accept: WORKBOOK_TYPE, ...authHeaders() },
    });

    if (!response.ok) {
      // The refusals are JSON and they name the setting that is missing, so they
      // are worth surfacing verbatim rather than as "download failed".
      let message = response.statusText;
      let body: Record<string, unknown> | undefined;
      try {
        body = (await response.json()) as Record<string, unknown>;
        if (typeof body.message === 'string') message = body.message;
      } catch {
        // Not JSON. The status carries it.
      }
      throw new ApiError(response.status, message, body);
    }

    downloadBlob(
      filenameFrom(response.headers.get('content-disposition')) ?? `tilitykset-${month}.xlsx`,
      await response.blob(),
    );
  },

  /** The tenant's VAT position. migration 0073. */
  taxSettings: () => request<TaxSettings>('/v1/commerce/tax-settings'),

  /**
   * Record when the shop became VAT-liable, or that it never has.
   *
   * `null` is a value here and not an omission: "this company has never been in
   * the VAT register" is a state somebody chooses.
   */
  setVatRegisteredFrom: (vatRegisteredFrom: string | null) =>
    send<TaxSettings>('/v1/commerce/tax-settings', {
      method: 'PUT',
      body: { vatRegisteredFrom },
    }),

  /**
   * The response carries the secret. It is the only response that ever will —
   * there is no endpoint that can show it again, by design.
   */
  createApiKey: (input: {
    name: string;
    roles: string[];
    expiresInDays: number | null;
    acknowledgeNoExpiry?: boolean;
  }) => send<CreatedApiKey>('/v1/settings/api-keys', { method: 'POST', body: input }),

  // --- printing (§11.6, migration 0077) -------------------------------------

  printDevices: () => request<{ devices: PrintDevice[] }>('/v1/printing/devices'),

  /**
   * Published builds of the agent.
   *
   * Public on the API, which is why the download below can be a plain link — an
   * installer is not a secret, and what makes an update safe is the signature
   * the agent verifies, not who was allowed to fetch it.
   */
  agentReleases: () => request<{ releases: AgentRelease[] }>('/v1/printing/agent/releases'),

  /**
   * Register a printer. The reply carries the pairing code, once.
   *
   * There is no endpoint that can be asked for it again — the row holds a
   * digest — so a caller that loses it asks for a new one with `pairDevice`.
   */
  createPrintDevice: (input: { code: string; name: string; site?: string | null }) =>
    send<PairedDevice>('/v1/printing/devices', { method: 'POST', body: input }),

  /** A fresh pairing code. Invalidates whatever key the old machine held. */
  pairPrintDevice: (id: string) =>
    send<PairedDevice>(`/v1/printing/devices/${encodeURIComponent(id)}/pair`, { method: 'POST' }),

  /** Revoke, not delete: the jobs it printed still point at it. */
  revokePrintDevice: (id: string) =>
    send<PrintDevice>(`/v1/printing/devices/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  printJobs: (query: { device?: string; state?: PrintJob['state']; limit?: number } = {}) =>
    request<{ jobs: PrintJob[] }>('/v1/printing/jobs', {
      device: query.device,
      state: query.state,
      limit: query.limit,
    }),

  /**
   * Queue a label.
   *
   * `data` is the template's `{{placeholder}}` values — this client fills them
   * in because it is the screen that knows what a product or a location is.
   * `platform/` may not import from `catalog` or `warehouse`, so the API
   * cannot look any of it up. The Data Matrix payload is the exception and is
   * composed server-side; anything sent as `code` is overwritten.
   */
  queueLabel: (input: {
    device: string;
    template: PrintJob['template'];
    data: Record<string, string | number | null>;
    copies?: number;
  }) => send<PrintJob>('/v1/printing/jobs', { method: 'POST', body: input }),

  /**
   * A picture of the label, before a roll is spent on it.
   *
   * A FETCH RETURNING A BLOB, not `send`, because the body is a PNG. The same
   * reason `downloadSettlementWorkbook` is a fetch: this app authenticates with
   * a bearer token held in memory, so an `<img src>` pointed at the endpoint
   * would carry no headers and answer 401 — exactly how the settlement workbook
   * shipped broken. The caller turns the blob into an object URL and revokes it.
   *
   * The server runs the print path to make it, refusal included, so a label
   * that cannot print does not preview either.
   */
  previewLabel: async (
    template: PrintJob['template'],
    data: Record<string, string | number | null>,
  ): Promise<Blob> => {
    const response = await fetch('/v1/printing/preview', {
      method: 'POST',
      headers: { Accept: 'image/png', 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ template, data }),
    });

    if (!response.ok) {
      // "This label draws within 1.5 mm of the edge of the roll" is the message
      // somebody needs, and it arrives as JSON. Worth surfacing verbatim.
      let message = response.statusText;
      let body: Record<string, unknown> | undefined;
      try {
        body = (await response.json()) as Record<string, unknown>;
        if (typeof body.message === 'string') message = body.message;
      } catch {
        // Not JSON. The status carries it.
      }
      throw new ApiError(response.status, message, body);
    }
    return response.blob();
  },

  cancelPrintJob: (id: string) =>
    send<PrintJob>(`/v1/printing/jobs/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),

  revokeApiKey: (id: string) =>
    send<void>(`/v1/settings/api-keys/${encodeURIComponent(id)}/revoke`, { method: 'POST' }),

  // --- orders (§4.5) --------------------------------------------------------

  orders: (query: OrderQuery = {}) =>
    request<Page<OrderSummary>>('/v1/orders', {
      ...orderParams(query),
      limit: query.limit ?? 50,
      cursor: query.cursor,
      dir: query.dir,
    }),

  orderCount: (filters: OrderFilters = {}) =>
    request<{ total: number }>('/v1/orders/count', orderParams(filters)),

  /**
   * One order.
   *
   * `channel` is optional and usually unnecessary — §4.5.3 numbers orders per
   * channel, so a shop with one, or one with a prefix on each, is unambiguous.
   * It matters for the shop with neither, where two orders are called "1" and
   * the API answers 409 `order-number-ambiguous` rather than picking one.
   */
  order: (orderNo: string, channel?: string) =>
    request<OrderDetail>(`/v1/orders/${encodeURIComponent(orderNo)}`, { channel }),

  /** D17 — one axis, with the reason that will have to explain it later. */
  transitionOrder: (
    orderNo: string,
    move: { axis: OrderAxis; to: string; reason: string },
    channel?: string,
  ) =>
    send<OrderDetail>(
      `/v1/orders/${encodeURIComponent(orderNo)}/transition` +
        (channel ? `?channel=${encodeURIComponent(channel)}` : ''),
      { method: 'POST', body: move },
    ),

  // --- customers (§4.3) -----------------------------------------------------

  customers: (query: { q?: string; limit?: number; cursor?: string } = {}) =>
    request<Page<CustomerListItem>>('/v1/customers', {
      q: query.q,
      limit: query.limit ?? 50,
      cursor: query.cursor,
    }),

  customerCount: (q?: string) => request<{ total: number }>('/v1/customers/count', { q }),

  customer: (id: string) => request<CustomerDetail>(`/v1/customers/${encodeURIComponent(id)}`),

  updateCustomer: (id: string, terms: CommercialTerms) =>
    send<Customer>(`/v1/customers/${encodeURIComponent(id)}`, { method: 'PATCH', body: terms }),

  /** §14.3 — irreversible. The row stays; the person goes. */
  eraseCustomer: (id: string) =>
    send<void>(`/v1/customers/${encodeURIComponent(id)}/erase`, { method: 'POST' }),

  // --- invoicing (§4.8) -----------------------------------------------------

  /** Every invoice, newest first. `unpaid` is what somebody chasing money reads. */
  invoices: (filter: { unpaid?: boolean } = {}) =>
    request<{ items: Invoice[] }>('/v1/invoices', {
      unpaid: filter.unpaid ? 'true' : undefined,
    }),

  /** Issued or sent, and past due. The chasing list. */
  outstandingInvoices: () => request<{ items: Invoice[] }>('/v1/invoices/outstanding'),

  /** One invoice, as the document. See `InvoiceDocument`. */
  invoice: (id: string) => request<InvoiceDocument>(`/v1/invoices/${encodeURIComponent(id)}`),

  invoicesForOrder: (orderId: string) =>
    request<{ items: Invoice[] }>(`/v1/invoices/for-order/${encodeURIComponent(orderId)}`),

  issueInvoice: (orderId: string) =>
    send<Invoice>('/v1/invoices', { method: 'POST', body: { orderId } }),

  creditInvoice: (id: string, lines: CreditLine[]) =>
    send<Invoice>(`/v1/invoices/${encodeURIComponent(id)}/credit`, {
      method: 'POST',
      body: { lines },
    }),

  markInvoicePaid: (id: string) =>
    send<Invoice>(`/v1/invoices/${encodeURIComponent(id)}/paid`, { method: 'POST', body: {} }),

  // --- reviews (§4.10) ------------------------------------------------------

  pendingReviews: (limit = 50) => request<{ items: Review[] }>('/v1/reviews/pending', { limit }),

  moderateReview: (id: string, decision: 'published' | 'rejected') =>
    send<Review>(`/v1/reviews/${encodeURIComponent(id)}/moderate`, {
      method: 'POST',
      body: { decision },
    }),

  /** §4.10 — the sentence a verified badge is lawful with, per locale. */
  setReviewPolicy: (locale: string, statement: string) =>
    send<{ ok: true }>('/v1/reviews/policy', { method: 'POST', body: { locale, statement } }),

  // --- campaigns and coupons (§4.7) -----------------------------------------

  promotions: (q?: string) => request<{ items: Promotion[] }>('/v1/promotions', { q }),

  createPromotion: (input: PromotionInput) =>
    send<Promotion>('/v1/promotions', { method: 'POST', body: input }),

  /**
   * The whole campaign, not a patch.
   *
   * The API takes a PUT for a reason worth repeating here: `value` means basis
   * points or minor units depending on `kind`, so a half-object validated
   * against the half still stored is how 5000 % gets in behind a kind change.
   */
  savePromotion: (id: string, input: PromotionInput) =>
    send<Promotion>(`/v1/promotions/${encodeURIComponent(id)}`, { method: 'PUT', body: input }),

  /** Which orders used it. The next click from "4 uses, 12,00 € given away". */
  promotionRedemptions: (id: string) =>
    request<{ items: PromotionRedemption[] }>(
      `/v1/promotions/${encodeURIComponent(id)}/redemptions`,
    ),

  deletePromotion: (id: string) =>
    send<void>(`/v1/promotions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

/**
 * Order filters to query-string values.
 *
 * Separate from the paging keys for the same reason `filterParams` is in the
 * catalog: the count endpoint takes the filters and nothing else, and two
 * hand-maintained copies of the same mapping is how a filter ends up applying
 * to the list and not to the number above it.
 */
function orderParams(filters: OrderFilters): Record<string, string | undefined> {
  return {
    q: filters.q,
    lifecycle: filters.lifecycle,
    payment: filters.payment,
    fulfilment: filters.fulfilment,
    channel: filters.channel,
    customer: filters.customer,
    from: filters.from,
    to: filters.to,
  };
}

// --- orders (§4.5, D17) ------------------------------------------------------

/** A keyset page. The cursor is opaque and belongs to one ordering. */
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/**
 * D17 — three independent axes, and NEVER a combined status.
 *
 * The API refuses to derive one and so does this file. Any word the screen
 * shows is assembled at the point of display, where it is obvious that it is a
 * summary of three facts rather than a fourth fact of its own.
 */
export type OrderAxis = 'lifecycle' | 'payment' | 'fulfilment';

export const ORDER_STATES: Record<OrderAxis, readonly string[]> = {
  lifecycle: ['draft', 'placed', 'closed', 'cancelled'],
  payment: ['unpaid', 'authorized', 'paid', 'partially_refunded', 'refunded', 'failed', 'expired'],
  fulfilment: [
    'not_required',
    'unfulfilled',
    'allocated',
    'picking',
    'packed',
    'partially_shipped',
    'shipped',
    'delivered',
    'returned',
    'partially_returned',
  ],
};

export interface OrderFilters {
  q?: string;
  lifecycle?: string;
  payment?: string;
  fulfilment?: string;
  channel?: string;
  customer?: string;
  from?: string;
  to?: string;
}

export interface OrderQuery extends OrderFilters {
  limit?: number;
  cursor?: string;
  dir?: 'asc' | 'desc';
}

export interface OrderSummary {
  id: string;
  orderNo: string;
  /**
   * Placed to prove the shop works, not by a customer.  migration 0059
   *
   * The number already says `TEST-1`, which is the marking that travels
   * everywhere. This is what lets the row be styled as well, so the list does
   * not rely on somebody reading the prefix.
   */
  isTest: boolean;
  placedAt: string;
  source: string;
  channelCode: string;
  email: string;
  customerId: string | null;
  customerName: string | null;
  lifecycle: string;
  paymentState: string;
  fulfilmentState: string;
  currency: string;
  grandTotal: number;
  lineCount: number;
}

export interface OrderLine {
  lineNo: number;
  sku: string;
  name: string;
  qty: number;
  unitPriceNet: number;
  unitPriceGross: number;
  taxRateBp: number;
  lineDiscount: number;
  lineNet: number;
  lineTax: number;
  lineGross: number;
  variantId: string | null;
  /**
   * Where the product lives NOW, for linking into the catalog.
   *
   * Not `sku` above: that is the snapshot the order was sold under (§4.5), and
   * a rename moves one without touching the other. Null when the line never
   * matched — an imported order can name a SKU this shop does not stock.
   */
  productSku: string | null;
  image: { url: string; previewUrl: string; alt: string | null } | null;
}

/** A payment taken somewhere else — Stripe, through the storefront's checkout. */
export interface OrderPayment {
  method: string;
  methodLabel: string | null;
  reference: string | null;
  paidAt: string;
  amount: number;
  fee: number | null;
  net: number | null;
}

export interface OrderEvent {
  axis: OrderAxis;
  fromState: string | null;
  toState: string;
  actor: string;
  reason: string | null;
  integrationRef: string | null;
  occurredAt: string;
}

/** Who the parcel is from. Printed on the packing slip. */
export interface OrderShop {
  name: string;
  logoUrl: string | null;
  legalFooter: string | null;
  supportEmail: string | null;
  supportPhone: string | null;
  locale: string;
}

export interface OrderDetail extends OrderSummary {
  subtotalNet: number;
  discountTotal: number;
  shippingNet: number;
  taxTotal: number;
  billTo: Record<string, unknown> | null;
  shipTo: Record<string, unknown> | null;
  consentSnapshot: Record<string, unknown> | null;
  shop: OrderShop;
  lines: OrderLine[];
  payments: OrderPayment[];
  events: OrderEvent[];
}

// --- customers (§4.3) --------------------------------------------------------

export interface Customer {
  id: string;
  subject: string | null;
  email: string;
  name: string | null;
  profileCode: string | null;
  businessName: string | null;
  taxId: string | null;
  termsDays: number | null;
  creditLimitMinor: number | null;
}

export interface CustomerListItem extends Customer {
  orderCount: number;
  lastOrderAt: string | null;
}

export interface CustomerAddress {
  id: string;
  kind: 'billing' | 'shipping';
  label: string | null;
  recipient: string;
  line1: string;
  line2: string | null;
  postalCode: string;
  city: string;
  country: string;
  phone: string | null;
  isDefault: boolean;
}

export interface CustomerDetail extends Customer {
  addresses: CustomerAddress[];
}

/** `null` clears a field; leaving the key out leaves the field alone. */
export interface CommercialTerms {
  name?: string | null;
  phone?: string | null;
  profileCode?: string | null;
  businessName?: string | null;
  taxId?: string | null;
  termsDays?: number | null;
  creditLimitMinor?: number | null;
}

// --- invoicing (§4.8) --------------------------------------------------------

export interface InvoiceLine {
  lineNo: number;
  description: string;
  qty: number;
  unitNet: number;
  taxRateBp: number;
  net: number;
  tax: number;
  gross: number;
  /** The order line this was raised from, when it came from an order. */
  orderLineId: string | null;
}

export interface Invoice {
  id: string;
  invoiceNo: string | null;
  /**
   * Who raised it. migration 0074.
   *
   * `recorded` means somebody raised it outside lasti and a person entered what
   * it says — lasti holds a reference and never pushes it anywhere.
   */
  origin: 'minted' | 'recorded';
  kind: 'invoice' | 'credit_note';
  creditsInvoiceId: string | null;
  orderId: string | null;
  customerId: string | null;
  state: 'draft' | 'issued' | 'sent' | 'paid' | 'credited' | 'void';
  overdue: boolean;
  currency: string;
  net: number;
  tax: number;
  gross: number;
  termsDays: number | null;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  lines: InvoiceLine[];
}

/**
 * An invoice as a document: the row, plus who it is to.
 *
 * The address comes from the ORDER, frozen at placement (§4.5) — an invoice
 * that re-read the customer would print a different address every time
 * somebody moved house.
 */
export interface InvoiceDocument extends Invoice {
  orderNo: string | null;
  channelCode: string | null;
  billTo: Record<string, unknown> | null;
  customerName: string | null;
  email: string | null;
  creditsInvoiceNo: string | null;
}

export interface CreditLine {
  description: string;
  qty: number;
  /** Minor units, exclusive of VAT. Positive: the direction is the document. */
  unitNet: number;
  taxRateBp: number;
}

// --- reviews (§4.10) ---------------------------------------------------------

export interface Review {
  id: string;
  productId: string;
  /** What is being reviewed. A moderator decides about a product, not a uuid. */
  productSku: string;
  productName: string | null;
  locale: string;
  authorName: string;
  rating: number;
  title: string | null;
  body: string;
  /** Decided by the API from a purchase, never asserted by anyone. */
  verified: boolean;
  state: 'pending' | 'published' | 'rejected';
  submittedAt: string;
}

// --- campaigns and coupons (§4.7) ---------------------------------------------

export type PromotionKind = 'percent' | 'amount' | 'free_shipping';
export type PromotionScope = 'cart' | 'line' | 'shipping';

/**
 * Where a campaign is in its life.
 *
 * Derived by the API from the dates, the switch and the redemption count —
 * there is no column, so it cannot be stale. The screen shows it and never
 * computes it, which is the same rule from the other side.
 */
export type PromotionState = 'live' | 'scheduled' | 'expired' | 'used-up' | 'off';

export interface PromotionConditions {
  /** Minor units, exclusive of VAT. */
  minSubtotalNet?: number;
  productIds?: string[];
  categoryCodes?: string[];
}

export interface PromotionInput {
  /** Null means automatic: it applies without anybody typing anything. */
  code: string | null;
  name: string;
  kind: PromotionKind;
  scope: PromotionScope;
  /** Basis points for `percent`, minor units for `amount`, unused otherwise. */
  value: number;
  conditions: PromotionConditions;
  validFrom: string | null;
  validTo: string | null;
  usageLimit: number | null;
  usagePerCustomer: number | null;
  stacking: 'exclusive' | 'stackable';
  priority: number;
  /** Empty means every channel. */
  channelCodes: string[];
  active: boolean;
}

/**
 * One use of a campaign.  §4.7
 *
 * `amountMinor` is what it was worth on THAT order — a 10 % coupon is a
 * different number on every basket, and the sum of these is the answer to
 * "what did this campaign cost us".
 */
export interface PromotionRedemption {
  orderNo: string;
  /** An order number names one order only with its shop (§4.5.3). */
  channelCode: string;
  placedAt: string;
  amountMinor: number;
  email: string;
  customerName: string | null;
}

export interface Promotion extends PromotionInput {
  id: string;
  state: PromotionState;
  /** Orders that used it, and what it took off them in total. */
  redemptions: number;
  spentMinor: number;
  createdAt: string;
}

// --- settings (§5.1) ---------------------------------------------------------

/** One stop on the walk. See `warehouse/pick-list.repository.ts`. */
export interface PickLine {
  locationPath: string | null;
  locationCode: string | null;
  pickSequence: number | null;
  sku: string;
  barcode: string | null;
  name: string | null;
  qty: number;
  /** Which orders these units are for, so the packing bench does not guess. */
  orders: { orderNo: string; qty: number }[];
  /** What the ledger expects to be in that location. */
  onHandThere: number | null;
  alternatives: number;
}

export interface PickList {
  siteCode: string;
  siteName: string;
  orderNos: string[];
  lines: PickLine[];
  unlocated: number;
}

/** One stop on the walk. See `warehouse/pick-list.repository.ts`. */
export interface PickLine {
  locationPath: string | null;
  locationCode: string | null;
  pickSequence: number | null;
  sku: string;
  barcode: string | null;
  name: string | null;
  qty: number;
  /** Which orders these units are for, so the packing bench does not guess. */
  orders: { orderNo: string; qty: number }[];
  /** What the ledger expects to be in that location. */
  onHandThere: number | null;
  alternatives: number;
}

export interface PickList {
  siteCode: string;
  siteName: string;
  orderNos: string[];
  lines: PickLine[];
  unlocated: number;
}

export type QueueStatus = 'pending' | 'pushed' | 'failed' | 'all';

export interface StorefrontSettings {
  channelCode?: string;
  platform: string;
  baseUrl: string;
  /** Must match the shop's own tax setting; the adapter refuses rather than converting. */
  pricesIncludeTax: boolean;
  requestsPerSecond: number;
  /** Names a row in `tenant_credential`, never a value. */
  credentialRef: string | null;
  imageCredentialRef: string | null;
  enabled: boolean;
}

export interface QueuedProduct {
  sku: string;
  productSku: string | null;
  status: string;
  attempts: number;
  lastError: string | null;
  nextAttemptAt: string;
  lastPushedAt: string | null;
  externalId: string | null;
}

export interface IntegrationEvent {
  id: string;
  source: string;
  level: 'info' | 'warn' | 'error';
  subject: string | null;
  message: string;
  detail: Record<string, unknown> | null;
  at: string;
}

export interface ApiKey {
  id: string;
  name: string;
  /** `lasti_sk_` plus a few characters — enough to recognise, not to use. */
  prefix: string;
  roles: string[];
  createdAt: string;
  createdBy: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  status: 'active' | 'expired' | 'revoked';
}

export interface CreatedApiKey {
  id: string;
  name: string;
  prefix: string;
  expiresAt: string | null;
  /** Shown once. Never stored by the client, never fetched again. */
  token: string;
}

// --- printing (§11.6, migration 0077) ----------------------------------------

export interface PrintDevice {
  id: string;
  /** The tenant's own word for a bench: 'pakkaamo', 'goods-in'. */
  code: string;
  name: string;
  siteCode: string | null;
  model: 'brother_ql';
  media: '62mm';
  /** What the agent last reported about itself. Self-reported, so advisory. */
  agentVersion: string | null;
  hostname: string | null;
  printerStatus: string | null;
  lastSeenAt: string | null;
  enrolledAt: string | null;
  keyPrefix: string | null;
  pairingExpiresAt: string | null;
  revokedAt: string | null;
  /** Derived by the API so a list and a detail screen cannot disagree. */
  state: 'pairing' | 'pairing_expired' | 'online' | 'offline' | 'revoked';
  queued: number;
}

/** The pairing code is in this response and in no other. */
export interface AgentRelease {
  version: string;
  /** `windows-x86_64`, as Tauri names a target. */
  target: string;
  filename: string;
  sha256: string;
  sizeBytes: number;
  /** Absent means downloadable but not auto-updatable. */
  signature: string | null;
  notes: string | null;
  publishedAt: string;
}
export interface PairedDevice {
  device: PrintDevice;
  pairingCode: string;
}

export interface PrintJob {
  id: string;
  deviceId: string;
  deviceCode: string;
  template: 'part' | 'bin';
  data: Record<string, string | number | null>;
  copies: number;
  state: 'queued' | 'claimed' | 'printed' | 'failed' | 'cancelled';
  attempts: number;
  error: string | null;
  requestedBy: string;
  createdAt: string;
  settledAt: string | null;
}

export interface UploadedMedia {
  mediaId: string;
  sha256: string;
  /** The tenant already had these exact bytes; no new object was written. */
  deduplicated: boolean;
}

/** Minor units to a human figure. Never used for arithmetic — display only. */
export function formatMoney(minor: number, currency: string): string {
  return new Intl.NumberFormat('fi-FI', { style: 'currency', currency }).format(minor / 100);
}
